import React from 'react';
import { PlusCircle, MinusCircle, CalendarDays } from 'lucide-react';

/**
 * Компонент секции операций с датой
 */
const OperationSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  timeUnits
}) => {
  return (
    <div className="mb-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <CalendarDays className="w-5 h-5 mr-2 text-cyan-500" />
        Операция с датой
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип операции */}
        <div>
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Операция
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="inline-flex items-center p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="add"
                checked={formData.mode === 'add'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm flex items-center">
                <PlusCircle className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-gray-700 dark:text-gray-300">Добавить</span>
              </span>
            </label>
            <label className="inline-flex items-center p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors cursor-pointer">
              <input
                type="radio"
                name="mode"
                value="subtract"
                checked={formData.mode === 'subtract'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm flex items-center">
                <MinusCircle className="w-4 h-4 text-red-500 mr-1" />
                <span className="text-gray-700 dark:text-gray-300">Вычесть</span>
              </span>
            </label>
          </div>
          {errors.mode && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.mode}
            </p>
          )}
        </div>

        {/* Значение и единица измерения */}
        <div className="flex gap-2">
          <div className="w-1/3">
            <label htmlFor="value" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество
            </label>
            <input
              type="number"
              id="value"
              name="value"
              min="0"
              max="10000"
              value={formData.value}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.value ? 'input-error' : ''}`}
              placeholder="Значение"
            />
            {errors.value && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.value}
              </p>
            )}
          </div>
          <div className="w-2/3">
            <label htmlFor="unit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Единица измерения
            </label>
            <select
              id="unit"
              name="unit"
              value={formData.unit}
              onChange={handleInputChange}
              className={`form-select w-full ${errors.unit ? 'input-error' : ''}`}
            >
              {timeUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
            {errors.unit && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.unit}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Пример интерпретации */}
      <div className="mt-4 bg-white dark:bg-gray-700 p-3 rounded-lg border border-cyan-100 dark:border-cyan-800 text-sm">
        <p className="text-gray-700 dark:text-gray-300">
          <span className="font-medium">Интерпретация: </span>
          {formData.mode === 'add' ? 'Добавить' : 'Вычесть'} {formData.value} {' '}
          {getUnitDisplay(formData.value, formData.unit, timeUnits)} к начальной дате
          {formData.unit === 'workingDays' && ' (только рабочие дни)'}
        </p>
      </div>
    </div>
  );
};

// Вспомогательная функция для корректного отображения единиц измерения
const getUnitDisplay = (value, unitId, timeUnits) => {
  const unit = timeUnits.find(u => u.id === unitId);
  if (!unit) return '';

  const count = parseInt(value, 10);
  
  // Возвращаем корректную форму в зависимости от числа
  if (count === 1) {
    return unit.name.toLowerCase();
  }
  
  if (count >= 2 && count <= 4) {
    return unit.namePlural.toLowerCase();
  }
  
  return unit.nameGenitive.toLowerCase();
};

export default OperationSection;